import { I18n } from "@assets/strings";
import React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import defaultShareroom from "@assets/images/default_shareroom.png";
import { Path } from "@core/routing/path";
import { Group } from "@domain/group/group";
import { ImageVersions } from "@domain/media/dtos";
import { useHover } from "@utils/hooks";
import { theme } from "@ui/styles/theme";
import { BackgroundImage } from "@ui/components/backgroundImage";
import { CategoryLabel } from "@ui/components/categoryLabel";
import { Clamp } from "@ui/components/containers/clamp";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { CommunityIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { MembersButton } from "@ui/components/profile/community/membersButton";
import { AccessModeIndicator } from "./accessModeIndicator";
import { JoinGroupThumbnailButton } from "./joinGroupThumbnailButton";

interface DiscussionThumbnailSmallProps extends React.HTMLAttributes<HTMLDivElement> {
	discussion: Group;
	bannerHeight?: number;
}
export const DiscussionThumbnailSmall: React.FC<DiscussionThumbnailSmallProps> = ({
	discussion,
	bannerHeight,
	...htmlProps
}) => {
	const { name, description, administrators: teachersList, categories, memberCounts, banner } = discussion;
	const memberStudentsCount = memberCounts.MEMBER ?? 0;
	const isOpen = discussion.accessMode === "OPEN";
	const { isHovered, ref } = useHover();

	return (
		<Container {...htmlProps} ref={ref}>
			<Thumbnail>
				<Link to={Path.Discussion(discussion)}>
					<StyledBackgroundImage
						image={banner}
						backdrop
						height={310}
						defaultUrl={!banner ? defaultShareroom : undefined}
						size={ImageVersions.MEDIUM}
						isHovered={isHovered}
					/>
				</Link>

				<Infos>
					<Title size={2}>{name}</Title>
					<Description size={2} className="info">
						{description}
					</Description>
					<MembersCount>
						{I18n.t("group.DISCUSSION_GROUP.thumbnail.members_count", { count: memberStudentsCount })}
					</MembersCount>
					<TeachersLabel>
						<b>{I18n.t("group.DISCUSSION_GROUP.thumbnail.animated_by")}</b>
					</TeachersLabel>
					<MembersButton sample={teachersList} count={teachersList.length} spacing={-13} imageSize={"M"} />
					<JoinButton group={discussion} />
				</Infos>
				<StyledAccessModeIndicator closed={!isOpen} />
				{categories[0] && categories[0] !== "OTHERS" && (
					<Category
						category={categories[0]}
						isSmall={true}
						max_temper={8}
						renderIcons={() => <CommunityIcon color={theme.colors.white} />}
					/>
				)}
			</Thumbnail>
		</Container>
	);
};

const Container = styled(RoundedBlock)`
	position: relative;
	display: flex;
	flex-direction: column;
	border-radius: 12px;
	cursor: pointer;
	overflow: hidden;
	background-color: ${({ theme }) => theme.colors.backgroundPrimary};

	@media (max-width: 900px) {
		padding-top: 0;
	}
	height: 310px;
	width: 250px;
`;

const Thumbnail = styled(RoundedBlock)`
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	border-width: 0px;

	> a {
		width: 100%;
		height: 100%;
	}
`;

const StyledBackgroundImage = styled(BackgroundImage)<{ height?: number }>`
	height: ${({ height = 255 }) => height}px;
`;

const Infos = styled.div`
	${({ theme }) => theme.textStyles.text};
	color: ${({ theme }) => theme.colors.white};
	padding: 25px 15px 15px 15px;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: flex-end;
	gap: 10px;
	position: absolute;
	top: 15px;
	left: 0;
	right: 0;
	bottom: 0;
	pointer-events: none;
`;

const TeachersLabel = styled.div`
	${({ theme }) => theme.textStyles.primary};
	color: ${theme.colors.white};
	font-size: O.5rem;
	font-weight: 700;
`;

const Title = styled(Clamp)`
	margin-top: 10px;
	font-size: 1.25rem;
	color: ${theme.colors.white};
`;

const Description = styled(Clamp)`
	font-size: 0.8125rem;
	font-weight: 500;
`;

const MembersCount = styled.div`
	${row(0, undefined, "center")};
	${({ theme }) => theme.textStyles.secondary};
	gap: 4px;
	width: 100%;
`;

const Category = styled(CategoryLabel)`
	position: absolute;
	top: 15px;
	left: 20px;
`;

const StyledAccessModeIndicator = styled(AccessModeIndicator)`
	position: absolute;
	top: 18px;
	right: 20px;
`;

const JoinButton = styled(JoinGroupThumbnailButton)`
	margin-top: 10px;
`;
